import React from "react";
import styled from "styled-components";
import Input from "../../common/components/elements/input/Input";
import PhoneIcon from "../../common/assets/phone.svg";
import ChatIcon from "../../common/assets/chat.svg";
import QuestionIcon from "../../common/assets/question.svg";
import {formatPrice} from "src/core/common/price";
import Collapsible from "../../common/components/utils/Collapsible";
import {Plus} from "styled-icons/boxicons-regular/Plus";
import {Minus} from "styled-icons/boxicons-regular/Minus";
import Separator from "../../common/components/elements/Separator";
import {Section} from "../../common/components/elements/container/section";
import useSiteOptions from "src/core/sites/hooks/useSiteOptions";

export const TitleLabel = styled.h3`
  font-size: ${({theme}) => theme.v2.typography.title6.size.lg};
  font-weight: ${({theme}) => theme.v2.typography.title6.weight};

  color: #020202;
  margin: 0;
`;

export const PrimaryText = styled.p`
  font-size: ${({theme}) => theme.v2.typography.sizing.xl.lg};

  color: #020202;
  margin: 0;
`;

export const Label = styled.label`
  font-weight: ${({theme}) => theme.v2.typography.bodyText1.weight.heavy};
  font-size: ${({theme}) => theme.v2.typography.sizing.m.lg};
  color: #020202;
`;

export const SecondaryLabel = styled(Label)`
  color: #8d8d8d;
  font-weight: ${({theme}) => theme.v2.typography.bodyText1.weight.default};
  font-size: ${({theme}) => theme.v2.typography.sizing.xs.lg};
`;

export const Container = styled.div`
  display: flex;
`;

export const SectionTitle = styled.section`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-content: center;
`;

export const ScrollableSection = styled(Section)`
  width: 100%;
  overflow-y: scroll;
  position: relative;
  flex: 1 1 auto;
  > * {
    position: absolute;
  }

  &::-webkit-scrollbar {
    background-color: #fff;
    width: 16px;
  }

  &::-webkit-scrollbar-track {
    background-color: #fff;
  }

  &::-webkit-scrollbar-track:hover {
    background-color: #f4f4f4;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #babac0;
    border-radius: 16px;
    border: 5px solid #fff;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: #a0a0a5;
    border: 4px solid #f4f4f4;
  }

  &::-webkit-scrollbar-button {
    display: none;
  }
`;

export function CheckoutTextField({label, ...props}) {
  return (
    <CheckoutTextFieldContainer>
      {label && <Label>{label}</Label>}
      <Input {...props} />
    </CheckoutTextFieldContainer>
  );
}
const CheckoutTextFieldContainer = styled.div`
  display: flex;
  flex-direction: column;

  ${Label} {
    margin-bottom: 5px;
  }
`;

export function CheckoutSectionHeader({title, subtitle}) {
  return (
    <CheckoutSectionHeaderContainer>
      <TitleLabel>{title}</TitleLabel>
      <PrimaryText>{subtitle}</PrimaryText>
    </CheckoutSectionHeaderContainer>
  );
}
const CheckoutSectionHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  > *:not(:last-child) {
    margin-bottom: ${({theme}) => theme.v2.spacing(2)};
  }
`;

export function TipSectionHeader({title, subtitle}) {
  return (
    <TipSectionHeaderContainer>
      <TitleLabel>{title}</TitleLabel>
      <PrimaryText>{subtitle}</PrimaryText>
    </TipSectionHeaderContainer>
  );
}
const TipSectionHeaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  > *:not(:last-child) {
    margin-bottom: ${({theme}) => theme.v2.spacing(2)};
  }
`;

export function CheckoutTotals({
  totalPrice,
  subTotal,
  discountTotal,
  hasDiscount,
  creditCardFee,
  deliveryFee,
  taxTotal,
  taxDetails,
  style,
  discounts,
  isValid = true,
  className,
}) {
  const siteOptions = useSiteOptions();
  const transactionTypeText = siteOptions.getTransactionTypeText();

  function isPositivePrice(price) {
    if (isValid && price) {
      return price.amount > 0;
    } else {
      return false;
    }
  }
  return (
    <CheckoutTotalsContainer style={style} className={className}>
      {subTotal && (
        <CheckoutTotalLine
          label={"Subtotal"}
          value={formatValidPrice(subTotal, isValid)}
        />
      )}
      {isPositivePrice(creditCardFee) && (
        <CheckoutTotalLine
          label={transactionTypeText}
          value={formatValidPrice(creditCardFee, isValid)}
        />
      )}
      {isPositivePrice(deliveryFee) && (
        <CheckoutTotalLine
          label={"Delivery Fee"}
          value={formatValidPrice(deliveryFee, isValid)}
        />
      )}
      {hasDiscount && discountTotal && (
        <DiscountsSection
          isValid={isValid}
          discounts={discounts}
          discountTotal={discountTotal}
        />
      )}
      {taxTotal && (
        <TaxesSection isValid={isValid} taxDetails={taxDetails} taxTotal={taxTotal} />
      )}
      <CheckoutTotalContainer>
        <CheckoutTotalLineEntry>
          <CustomTitleLabel>Total</CustomTitleLabel>
        </CheckoutTotalLineEntry>
        <CheckoutTotalLineEntry>
          {totalPrice && <TitleLabel>{formatValidPrice(totalPrice, isValid)}</TitleLabel>}
        </CheckoutTotalLineEntry>
      </CheckoutTotalContainer>
    </CheckoutTotalsContainer>
  );
}
function formatValidPrice(price, isValid) {
  if (isValid) {
    return formatPrice(price);
  } else {
    return "-";
  }
}
function TaxesSection({isValid, taxTotal, taxDetails}) {
  return (
    <Collapsible>
      {({isCollapsed, toggle}) => (
        <>
          <CheckoutTotalLine
            label={"Total tax"}
            value={formatValidPrice(taxTotal, isValid)}
            onClick={toggle}
          >
            <>
              {taxDetails && (
                <ExpandableIconContainer>
                  {isCollapsed ? <PlusIcon /> : <MinusIcon />}
                </ExpandableIconContainer>
              )}
            </>
          </CheckoutTotalLine>
          {!isCollapsed && taxDetails && (
            <>
              <Separator />
              <CheckoutTaxesContainer>
                {taxDetails.getTaxesList().map((tax, index) => (
                  <TaxDetailLine
                    name={tax.getDisplayName()}
                    value={tax.getValue()}
                    key={index}
                  />
                ))}
              </CheckoutTaxesContainer>
            </>
          )}
        </>
      )}
    </Collapsible>
  );
}
function DiscountsSection({isValid, discountTotal, discounts}) {
  function getDiscountLabel(discount) {
    const labels = {
      coupon: "Coupon",
      reward: "Reward",
      group: "Group",
      automatic: "Automatic",
    };
    if (labels[discount.type]) {
      return `${labels[discount.type]} (${discount.name})`;
    } else if (discount.name) {
      return `Discount (${discount.name})`;
    } else {
      return "Discount";
    }
  }

  const hasDiscounts = discounts && discounts.length > 0;
  return (
    <Collapsible>
      {({isCollapsed, toggle}) => (
        <>
          <CheckoutTotalLine
            label={"Discount Total"}
            value={formatValidPrice(discountTotal, isValid)}
            onClick={hasDiscounts && toggle}
          >
            {hasDiscounts && (
              <ExpandableIconContainer>
                {isCollapsed ? <PlusIcon /> : <MinusIcon />}
              </ExpandableIconContainer>
            )}
          </CheckoutTotalLine>
          {!isCollapsed && (
            <>
              <Separator />
              <CheckoutTaxesContainer>
                {hasDiscounts &&
                  discounts.map((d, index) => (
                    <TaxDetailLine
                      name={getDiscountLabel(d)}
                      value={d.value}
                      key={index}
                    />
                  ))}
              </CheckoutTaxesContainer>
            </>
          )}
        </>
      )}
    </Collapsible>
  );
}
function CheckoutTotalLine({label, value, children, onClick, ...props}) {
  return (
    <CheckoutTotalLineContainer onClick={onClick ? onClick : undefined} {...props}>
      <CheckoutTotalLineEntry>
        <Label>{label}</Label>
      </CheckoutTotalLineEntry>
      <CheckoutTotalLineEntry>
        <Label>{value}</Label>
      </CheckoutTotalLineEntry>
      {children}
    </CheckoutTotalLineContainer>
  );
}

function TaxDetailLine({name, value, ...props}) {
  return (
    <CheckoutTaxesLineContainer {...props}>
      <CheckoutTotalLineEntry>
        <SecondaryLabel>{name}</SecondaryLabel>
      </CheckoutTotalLineEntry>
      <CheckoutTotalLineEntry>
        <SecondaryLabel>{formatPrice(value)}</SecondaryLabel>
      </CheckoutTotalLineEntry>
    </CheckoutTaxesLineContainer>
  );
}
const CheckoutTotalLineContainer = styled.div`
  display: flex;
  position: relative;
  align-items: center;

  ${({onClick}) => (onClick ? "cursor: pointer;" : "")}
  * {
    ${({onClick}) => (onClick ? "cursor: pointer;" : "")}
  }
`;
const CheckoutTaxesLineContainer = styled.div`
  display: flex;
  align-items: center;
`;
const CheckoutTotalContainer = styled(CheckoutTotalLineContainer)`
  margin-top: ${({theme}) => theme.v2.spacing(4)};
  margin-bottom: ${({theme}) => theme.v2.spacing(4)};
`;
const CheckoutTaxesContainer = styled.div`
  margin-top: ${({theme}) => theme.v2.spacing(2)};
  margin-bottom: ${({theme}) => theme.v2.spacing(2)};
`;
const CheckoutTotalLineEntry = styled.div`
  flex: 1;
`;
const CheckoutTotalsContainer = styled.div`
  ${CheckoutTotalLineContainer}:not(:last-child) {
    margin-bottom: ${({theme}) => theme.v2.spacing(2)};
  }
`;

const ExpandableIconContainer = styled.div`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translate3d(0, -50%, 0);
`;
const PlusIcon = styled(Plus)`
  height: 20px;
  width: 20px;
`;
const MinusIcon = styled(Minus)`
  height: 20px;
  width: 20px;
`;
const CustomTitleLabel = styled(TitleLabel)`
  @media (max-width: 428px) {
    font-size: ${({theme}) => theme.v2.typography.sizing.xl.lg};
  }
`;

export function QuestionsFooter() {
  return (
    <QuestionsFooterContainer>
      <QuestionsFooterHeader>
        <QuestionsFooterHeaderLine />
        <span>Questions?</span>
        <QuestionsFooterHeaderLine />
      </QuestionsFooterHeader>
      <FooterHelpList>
        <li>
          <FooterHelpEntry icon={PhoneIcon} label={"(415) 308-1778"} />
        </li>
        <li>
          <FooterHelpEntry icon={ChatIcon} label={"Live Chat"} />
        </li>
        <li>
          <FooterHelpEntry icon={QuestionIcon} label={"FAQ"} />
        </li>
      </FooterHelpList>
    </QuestionsFooterContainer>
  );
}
const QuestionsFooterContainer = styled.div`
  width: calc(100% - 32px);
  top: 100%;
  position: absolute;
  transform: translate3d(0, -100%, 0);
`;
const QuestionsFooterHeader = styled.div`
  color: #8d8d8d;
  text-align: center;
  display: flex;
  align-items: center;
  > span {
    font-size: ${({theme}) => theme.v2.typography.sizing.m.lg};
    padding: 0 16px;
  }
`;
const QuestionsFooterHeaderLine = styled(Separator)`
  width: 100%;
`;
const FooterHelpList = styled.ul`
  width: 100%;
  margin: 16px 0;
  padding: 0;
  list-style-type: none;
  display: flex;
  align-items: center;
  justify-content: space-evenly;
`;
function FooterHelpEntry({icon, label}) {
  return (
    <FooterHelpEntryContainer>
      <img src={icon} alt={""} />
      <span>{label}</span>
    </FooterHelpEntryContainer>
  );
}
const FooterHelpEntryContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding-bottom: 16px;
  > img {
    width: 24px;
    height: 24px;
  }
  > span {
    top: 100%;
    transform: translate3d(0, -100%, 0);
    text-align: center;
    width: 100px;
    position: absolute;
    font-size: ${({theme}) => theme.v2.typography.sizing.s.lg};
    color: #8d8d8d;
  }
`;
